/**
 * SigningSettings.tsx — V7-1-8 / ADR-0037.
 *
 * Workspace Settings: signing topology picker (gacpro keyless /
 * pull-sign-push / KMS adapter) and per-provider credential config.
 *
 * Route: /w/:slug/app/settings/signing (workspace-scoped, admin+).
 */

import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Radio,
  Select,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import {
  getSigningSettings,
  putSigningSettings,
  testSigningConnection,
  type SigningTopology,
  type SigningSettingsPayload,
} from '../lib/api';

// ---------------------------------------------------------------------------
// KMS topology helpers — core's SigningTopology is flat (each KMS provider
// is its own topology value). The UI groups the four KMS providers under
// one "KMS Adapter" radio and exposes a sub-selector.
// ---------------------------------------------------------------------------

/** The four KMS topology values from core. */
type KmsTopology = 'aws_kms' | 'azure_keyvault' | 'gcp_kms' | 'vault_transit';

const KMS_TOPOLOGIES: readonly KmsTopology[] = [
  'aws_kms', 'azure_keyvault', 'gcp_kms', 'vault_transit',
];

function isKmsTopology(t: SigningTopology): t is KmsTopology {
  return (KMS_TOPOLOGIES as readonly string[]).includes(t);
}

/** UI-level grouping: 3-way radio. */
type TopologyGroup = 'platform_keyless' | 'pull_sign_push' | 'kms';

function topologyToGroup(t: SigningTopology): TopologyGroup {
  if (isKmsTopology(t)) return 'kms';
  return t as TopologyGroup;
}

// ---------------------------------------------------------------------------
// Provider field definitions
// ---------------------------------------------------------------------------

interface ProviderField {
  key: string;
  label: string;
  required: boolean;
  secret: boolean;
  placeholder?: string;
}

const PROVIDER_FIELDS: Record<KmsTopology, ProviderField[]> = {
  aws_kms: [
    { key: 'role_arn', label: 'Role ARN', required: true, secret: false, placeholder: 'arn:aws:iam::123456789012:role/signing-role' },
    { key: 'key_id', label: 'Key ID', required: true, secret: false, placeholder: 'alias/my-signing-key or key ARN' },
    { key: 'region', label: 'Region', required: true, secret: false, placeholder: 'us-east-1' },
    { key: 'external_id', label: 'External ID', required: false, secret: false, placeholder: 'Optional external ID for cross-account' },
  ],
  azure_keyvault: [
    { key: 'vault_url', label: 'Vault URL', required: true, secret: false, placeholder: 'https://my-vault.vault.azure.net' },
    { key: 'tenant_id', label: 'Tenant ID', required: true, secret: false, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
    { key: 'client_id', label: 'Client ID', required: true, secret: false, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
    { key: 'client_secret', label: 'Client Secret', required: true, secret: true },
    { key: 'key_name', label: 'Key Name', required: true, secret: false, placeholder: 'my-signing-key' },
  ],
  gcp_kms: [
    { key: 'project_id', label: 'Project ID', required: true, secret: false, placeholder: 'my-gcp-project' },
    { key: 'location', label: 'Location', required: true, secret: false, placeholder: 'us-east1' },
    { key: 'key_ring', label: 'Key Ring', required: true, secret: false, placeholder: 'my-key-ring' },
    { key: 'key_name', label: 'Key Name', required: true, secret: false, placeholder: 'my-signing-key' },
    { key: 'key_version', label: 'Key Version', required: true, secret: false, placeholder: '1' },
    { key: 'service_account_json', label: 'Service Account JSON', required: true, secret: true },
  ],
  vault_transit: [
    { key: 'vault_url', label: 'Vault URL', required: true, secret: false, placeholder: 'https://vault.example.com:8200' },
    { key: 'role_id', label: 'Role ID', required: true, secret: false },
    { key: 'secret_id', label: 'Secret ID', required: true, secret: true },
    { key: 'key_name', label: 'Key Name', required: true, secret: false, placeholder: 'my-transit-key' },
    { key: 'mount_path', label: 'Mount Path', required: false, secret: false, placeholder: 'transit (default)' },
  ],
};

const PROVIDER_LABELS: Record<KmsTopology, string> = {
  aws_kms: 'AWS KMS',
  azure_keyvault: 'Azure Key Vault',
  gcp_kms: 'GCP Cloud KMS',
  vault_transit: 'HashiCorp Vault',
};

const TOPOLOGY_OPTIONS: { value: TopologyGroup; label: string; description: string }[] = [
  {
    value: 'platform_keyless',
    label: 'FedSec Keyless',
    description: 'FedSec signs with Sigstore keyless. No configuration needed.',
  },
  {
    value: 'pull_sign_push',
    label: 'Pull-Sign-Push',
    description: 'You sign releases offline with your own keys and push signatures back.',
  },
  {
    value: 'kms',
    label: 'KMS Adapter',
    description: 'FedSec signs using your cloud KMS. Configure credentials below.',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SigningSettings() {
  const { workspace } = useWorkspace();
  const slug = workspace.slug;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state — group is the 3-way radio; kmsTopology is the specific KMS provider.
  const [group, setGroup] = useState<TopologyGroup>('platform_keyless');
  const [kmsTopology, setKmsTopology] = useState<KmsTopology>('aws_kms');
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  /** The flat SigningTopology sent to / received from the API. */
  const resolvedTopology: SigningTopology = group === 'kms' ? kmsTopology : group;

  // Save/test state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Load current settings
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSigningSettings(slug)
      .then((data) => {
        if (cancelled) return;
        const g = topologyToGroup(data.topology);
        setGroup(g);
        if (g === 'kms' && isKmsTopology(data.topology)) {
          setKmsTopology(data.topology);
        }
        if (data.kms_credentials) setCredentials(data.kms_credentials);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    setTestResult(null);
    try {
      const payload: SigningSettingsPayload = { topology: resolvedTopology };
      if (group === 'kms') {
        payload.kms_credentials = credentials;
      }
      await putSigningSettings(slug, payload);
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testSigningConnection(slug, kmsTopology, credentials);
      setTestResult(result);
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : String(err) });
    } finally {
      setTesting(false);
    }
  };

  const updateCredential = (key: string, value: string) => {
    setSaveSuccess(false);
    setTestResult(null);
    setCredentials((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) return <Spinner label="Loading signing settings" />;

  if (error) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <PageHeader
          title="Signing"
          subtitle="Configure how releases are signed."
        />
        <Alert variant="error">Failed to load signing settings: {error}</Alert>
      </div>
    );
  }

  const currentFields = group === 'kms' ? PROVIDER_FIELDS[kmsTopology] : [];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 640 }}>
      <PageHeader
        title="Signing"
        subtitle="Configure how releases are signed."
      />

      {/* Topology picker */}
      <Card>
        <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
          <h3 style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-md)',
            fontWeight: 600,
            color: 'var(--text)',
          }}>
            Signing topology
          </h3>
          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-1)' }}>
            <legend style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-2xs)',
              color: 'var(--text-muted)',
              marginBottom: 'var(--space-2)',
            }}>
              Choose how releases are signed
            </legend>
            {TOPOLOGY_OPTIONS.map((opt) => (
              <Radio
                key={opt.value}
                name="signing-topology"
                value={opt.value}
                checked={group === opt.value}
                onChange={() => {
                  setGroup(opt.value);
                  setSaveSuccess(false);
                  setTestResult(null);
                }}
                label={opt.label}
                description={opt.description}
              />
            ))}
          </fieldset>
        </div>
      </Card>

      {/* KMS adapter config — shown only when group = kms */}
      {group === 'kms' && (
        <Card>
          <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-4)' }}>
            <h3 style={{
              margin: 0,
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-md)',
              fontWeight: 600,
              color: 'var(--text)',
            }}>
              KMS configuration
            </h3>

            <Field id="kms-provider" label="Provider">
              <Select
                value={kmsTopology}
                onChange={(e) => {
                  setKmsTopology(e.target.value as KmsTopology);
                  setCredentials({});
                  setTestResult(null);
                  setSaveSuccess(false);
                }}
              >
                {(Object.keys(PROVIDER_LABELS) as KmsTopology[]).map((p) => (
                  <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
                ))}
              </Select>
            </Field>

            {currentFields.map((field) => (
              <Field
                key={field.key}
                id={`kms-${field.key}`}
                label={field.label}
                required={field.required}
              >
                <Input
                  type={field.secret ? 'password' : 'text'}
                  value={credentials[field.key] ?? ''}
                  onChange={(e) => updateCredential(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  autoComplete="off"
                />
              </Field>
            ))}

            {/* Test connection */}
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <Button
                disabled={testing}
                onClick={handleTestConnection}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
              {testResult && (
                <span style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-3xs)',
                  color: testResult.ok ? 'var(--success-fg)' : 'var(--critical)',
                }}>
                  {testResult.message}
                </span>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Save */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <Button disabled={saving} onClick={handleSave}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
        {saveSuccess && (
          <Alert variant="success">Settings saved.</Alert>
        )}
        {saveError && (
          <Alert variant="error">{saveError}</Alert>
        )}
      </div>
    </div>
  );
}
